import React from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

interface QuotaPrediction {
  will_exceed_quotas: boolean;
  overages: Array<{
    resource: string;
    current: number;
    limit: number;
    over_by: number;
    message: string;
  }>;
  current_usage: {
    projects: number;
    architectures: number;
    deployments_this_month?: number;
  };
  target_limits: {
    projects: number;
    architectures: number;
    deployments_per_month?: number;
  };
}

interface DowngradeWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  targetPlan: string;
  effectiveDate: string;
  quotaPrediction: QuotaPrediction;
}

/**
 * Modal B - Avertissement "vous dépasserez les quotas"
 *
 * Affiché quand l'utilisateur programme un downgrade et le système estime
 * qu'il sera OVER_QUOTA au moment du passage.
 *
 * Spec: Message UX B
 */
const DowngradeWarningModal: React.FC<DowngradeWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  targetPlan,
  effectiveDate,
  quotaPrediction
}) => {
  if (!isOpen || !quotaPrediction.will_exceed_quotas) return null;

  const resourceLabels: Record<string, string> = {
    projects: 'Projets',
    architectures: 'Architectures',
    deployments: 'Déploiements / mois'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-full">
                <FiAlertTriangle className="text-orange-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Vous dépasserez les limites du plan {targetPlan.toUpperCase()}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Texte principal */}
          <p className="text-gray-700 mb-4">
            Au moment du passage au plan <strong>{targetPlan.toUpperCase()}</strong> le{' '}
            <strong>{effectiveDate}</strong>, votre organisation dépassera les quotas suivants :
          </p>

          {/* Encart "Dépassements" */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-orange-900 mb-3">Dépassements</p>
            <div className="space-y-2">
              {quotaPrediction.overages.map((overage, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">
                    {resourceLabels[overage.resource] || overage.resource}
                  </span>
                  <span className="text-sm font-medium">
                    <span className="text-orange-700">{overage.current}</span>
                    {' / '}
                    <span className="text-gray-600">{overage.limit}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Conséquences */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-2">Conséquences</p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Vous conserverez l'accès à toutes vos données en lecture.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 mt-0.5">⚠</span>
                <span>
                  La création de nouveaux projets/architectures/déploiements sera bloquée tant que vous êtes au-dessus des quotas.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">ℹ</span>
                <span>
                  Vous pourrez supprimer des éléments pour revenir sous les limites, ou repasser sur un plan supérieur.
                </span>
              </li>
            </ul>
          </div>

          {/* Boutons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              Garder mon plan
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium"
            >
              Continuer quand même
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DowngradeWarningModal;
